import { type AbstractMesh, Color3, Mesh, type Scene, StandardMaterial, VertexData } from "@babylonjs/core";

export const CHUNK = 64;
export const SPACING = 2;
export const VERTS = CHUNK / SPACING + 1;
const LOAD_RADIUS = 5;
const UNLOAD_RADIUS = 7;
const BUILDS_PER_FRAME = 2;

type Height = (x: number, z: number) => number;
interface Palette { grass: string; dirt: string; rock: string }

function hex(c: string): [number, number, number] {
  const n = parseInt(c.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Vertex colour from height and slope: rock on steep slopes, dirt in flat lowland, grass otherwise. */
export function vertexColor(h: number, slope: number, p: Palette): [number, number, number] {
  const grass = hex(p.grass), dirt = hex(p.dirt), rock = hex(p.rock);
  const dirtT = Math.max(0, Math.min(1, -h / 0.3)); // all dirt at h<=-0.3, all grass by h>=0 (flattened town ground is grass)
  const rockT = Math.max(0, Math.min(1, (slope - 0.45) / 0.3)); // 0 below slope 0.45, 1 above 0.75
  return mix(mix(grass, dirt, dirtT), rock, rockT);
}

export function chunkVertexData(cx: number, cz: number, height: Height, palette: Palette) {
  const ox = cx * CHUNK, oz = cz * CHUNK;
  const positions = new Float32Array(VERTS * VERTS * 3);
  const colors = new Float32Array(VERTS * VERTS * 4);
  const indices = new Uint32Array((VERTS - 1) * (VERTS - 1) * 6);
  let p = 0, c = 0;
  for (let iz = 0; iz < VERTS; iz++) {
    for (let ix = 0; ix < VERTS; ix++) {
      const lx = ix * SPACING, lz = iz * SPACING;
      const wx = ox + lx, wz = oz + lz;
      const h = height(wx, wz);
      positions[p++] = lx; positions[p++] = h; positions[p++] = lz;
      const dhdx = (height(wx + SPACING, wz) - height(wx - SPACING, wz)) / (2 * SPACING);
      const dhdz = (height(wx, wz + SPACING) - height(wx, wz - SPACING)) / (2 * SPACING);
      const [r, g, b] = vertexColor(h, Math.hypot(dhdx, dhdz), palette);
      colors[c++] = r; colors[c++] = g; colors[c++] = b; colors[c++] = 1;
    }
  }
  let k = 0;
  for (let iz = 0; iz < VERTS - 1; iz++) {
    for (let ix = 0; ix < VERTS - 1; ix++) {
      const a = iz * VERTS + ix, b = a + 1, cI = a + VERTS, d = cI + 1;
      // Babylon (left-handed, y up) treats clockwise-from-above as the front face; the other
      // order renders only the undersides once back-face culling is on.
      indices[k++] = a; indices[k++] = b; indices[k++] = cI;
      indices[k++] = b; indices[k++] = d; indices[k++] = cI;
    }
  }
  return { positions, indices, colors };
}

export class TerrainManager {
  private readonly chunks = new Map<string, Mesh>();
  private readonly queue: [number, number][] = [];
  private readonly material: StandardMaterial;

  constructor(
    private readonly scene: Scene,
    private readonly height: Height,
    private readonly palette: Palette,
    private readonly listeners: { created?(cx: number, cz: number): void; disposed?(cx: number, cz: number): void } = {},
  ) {
    this.material = new StandardMaterial("terrain", scene);
    this.material.specularColor = Color3.Black();
    this.material.backFaceCulling = true;
    scene.onBeforeRenderObservable.add(() => this.drain());
  }

  private key(cx: number, cz: number): string { return `${cx}:${cz}`; }

  /** Call every frame with the viewer position. */
  update(x: number, z: number): void {
    const ccx = Math.floor(x / CHUNK), ccz = Math.floor(z / CHUNK);
    for (let dx = -LOAD_RADIUS; dx <= LOAD_RADIUS; dx++) {
      for (let dz = -LOAD_RADIUS; dz <= LOAD_RADIUS; dz++) {
        if (Math.hypot(dx, dz) > LOAD_RADIUS) continue;
        const cx = ccx + dx, cz = ccz + dz;
        const k = this.key(cx, cz);
        if (!this.chunks.has(k) && !this.queue.some(([qx, qz]) => qx === cx && qz === cz)) this.queue.push([cx, cz]);
      }
    }
    this.queue.sort((a, b) => Math.hypot(a[0] - ccx, a[1] - ccz) - Math.hypot(b[0] - ccx, b[1] - ccz));
    for (const [k, mesh] of this.chunks) {
      const [cx, cz] = k.split(":").map(Number) as [number, number];
      if (Math.hypot(cx - ccx, cz - ccz) > UNLOAD_RADIUS) {
        mesh.dispose();
        this.chunks.delete(k);
        this.listeners.disposed?.(cx, cz);
      }
    }
  }

  private drain(): void {
    for (let i = 0; i < BUILDS_PER_FRAME && this.queue.length; i++) {
      const [cx, cz] = this.queue.shift()!;
      if (this.chunks.has(this.key(cx, cz))) continue;
      this.build(cx, cz);
    }
  }

  private build(cx: number, cz: number): void {
    const data = chunkVertexData(cx, cz, this.height, this.palette);
    const mesh = new Mesh(`terrain:${cx}:${cz}`, this.scene);
    const vd = new VertexData();
    vd.positions = data.positions;
    vd.indices = data.indices;
    vd.colors = data.colors;
    const normals: number[] = [];
    VertexData.ComputeNormals(data.positions, data.indices, normals);
    vd.normals = normals;
    vd.applyToMesh(mesh);
    mesh.convertToFlatShadedMesh();
    mesh.position.set(cx * CHUNK, 0, cz * CHUNK);
    mesh.material = this.material;
    mesh.checkCollisions = true;
    mesh.receiveShadows = true;
    mesh.isPickable = true;
    mesh.metadata = { terrain: true };
    mesh.freezeWorldMatrix();
    this.chunks.set(this.key(cx, cz), mesh);
    this.listeners.created?.(cx, cz);
  }

  meshes(): Mesh[] { return [...this.chunks.values()]; }
  isTerrain(m: AbstractMesh): boolean { return m.metadata?.terrain === true; }
}
